import { clsx } from 'clsx';

interface CriterionInputProps {
  criterionId: string;
  name: string;
  question: string;
  score: number | null;
  rationale: string;
  onScoreChange: (score: number) => void;
  onRationaleChange: (rationale: string) => void;
}

const SCORE_LABELS: Record<number, string> = {
  1: 'Poor',
  2: 'Below Average',
  3: 'Adequate',
  4: 'Good',
  5: 'Excellent',
};

export function CriterionInput({
  criterionId,
  name,
  question,
  score,
  rationale,
  onScoreChange,
  onRationaleChange,
}: CriterionInputProps) {
  const charCount = rationale.length;
  const maxChars = 500;
  const isOverLimit = charCount > maxChars;
  const isComplete = score !== null && rationale.length >= 1 && !isOverLimit;

  return (
    <div
      className={clsx(
        'p-4 border rounded-lg transition-colors',
        isComplete
          ? 'border-action/30 bg-action/5'
          : 'border-wolf-700/50 bg-wolf-900/30'
      )}
    >
      <div className="mb-3">
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-mono text-sm text-action">{criterionId}</span>
          <span className="text-wolf-100 font-medium">{name}</span>
        </div>
        <p className="text-wolf-400 text-sm">{question}</p>
      </div>

      {/* Score radio buttons */}
      <div className="mb-4">
        <label className="text-sm text-wolf-300 mb-2 block">Score</label>
        <div className="flex flex-wrap gap-2">
          {[1, 2, 3, 4, 5].map((value) => (
            <label
              key={value}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all',
                'border',
                score === value
                  ? 'border-action bg-action/20 text-action'
                  : 'border-wolf-700/50 bg-wolf-900/50 text-wolf-300 hover:border-wolf-500'
              )}
            >
              <input
                type="radio"
                name={`score-${criterionId}`}
                value={value}
                checked={score === value}
                onChange={() => onScoreChange(value)}
                className="sr-only"
              />
              <span className="font-mono font-bold">{value}</span>
              <span className="text-xs hidden sm:inline">({SCORE_LABELS[value]})</span>
            </label>
          ))}
        </div>
      </div>

      {/* Rationale textarea */}
      <div>
        <label className="text-sm text-wolf-300 mb-2 block">
          Rationale <span className="text-wolf-500">(required)</span>
        </label>
        <textarea
          value={rationale}
          onChange={(e) => onRationaleChange(e.target.value)}
          placeholder="Explain your score..."
          rows={3}
          className={clsx(
            'w-full px-3 py-2 rounded-lg bg-wolf-900/50 border text-wolf-100',
            'placeholder-wolf-500 resize-none',
            'focus:outline-none focus:ring-2 focus:ring-action/50',
            isOverLimit ? 'border-red-500' : 'border-wolf-700/50'
          )}
        />
        <div className="flex justify-end mt-1">
          <span
            className={clsx(
              'text-xs',
              isOverLimit ? 'text-red-400' : 'text-wolf-500'
            )}
          >
            {charCount}/{maxChars}
          </span>
        </div>
      </div>
    </div>
  );
}
